/**
 * _generic-bank.ts
 *
 * Bank-driven lesson driver. Loads a static JSON bank file, samples 10
 * distinct-surfaceVariant questions, and plays them through the standard
 * LessonController (2 warmup + 3 guide + 5 challenge).
 *
 * Used by `math-lesson.ts` for KPs that have a handwritten bank but no
 * generator (e.g. geometry / measurement KPs where parametric variants
 * are awkward). Mirrors the structure of `nature-lesson.ts` and
 * `social-lesson.ts` minus the per-KP url lookup (that belongs in the
 * subject dispatcher).
 */
import { LessonController, LessonPhases } from '../lesson';
import { QuizOverlay } from '../quiz/overlay';
import { showXpPopup, flashCard } from '../quiz/feedback';
import { getUiSoundPlayer } from '../ui-sounds';
import { loadBank, sampleBank } from '../content/bank-loader';
import {
  loadProgress,
  saveProgress,
  createFreshProgress,
  addFactionXP,
  skillUnlocked,
} from '../../shared/progress';
import { findOrCreateKP, recordAttempt } from '../../shared/student-kp';
import { FACTIONS, factionForSubject } from '../../shared/factions';
import { SKILL_CATALOG } from '../../shared/skills';
import { mountEscapeButton } from '../escape-button';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { isProMode } from '../settings';
import type { Subject } from '../../shared/types';
import type { LearnDoneCallback } from '../app';

export interface BankKpLessonOpts {
  subject: Subject;
  kpId: string;
  bankUrl: string;
  popupColor?: string;
}

export async function runBankKpLesson(
  container: HTMLElement,
  opts: BankKpLessonOpts,
  onDone: LearnDoneCallback,
): Promise<void> {
  const bank = await loadBank(opts.bankUrl);
  const sampled = sampleBank(bank, 10);
  const phases: LessonPhases = {
    warmup: sampled.slice(0, 2),
    guide: sampled.slice(2, 5),
    challenge: sampled.slice(5, 10),
  };

  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');
  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  // 預設 ON ⇒ skip SFX / flashCard / XP popup. Toggle via 設定 → 📚 教學介面.
  // Mirrors `_runner.ts` gating so bank-driven lessons match generator-driven ones.
  const pro = isProMode(progress);
  const kp = findOrCreateKP(progress, opts.kpId, opts.subject);

  let scoredTotal = 0;
  let scoredCorrect = 0;
  const lesson = new LessonController(phases, 2, (correct) => {
    if (lesson.state !== 'warmup') {
      scoredTotal += 1;
      if (correct) scoredCorrect += 1;
    }
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    saveProgress(progress!);
  });

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const currentQ = lesson.currentQuestion();
      const currentPhase = lesson.state;
      const result = lesson.submitAnswer(answer);
      // proMode ON → 不做整卡綠/紅閃 (考試介面只用 option-label tick 標示).
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
        if (currentPhase !== 'warmup') {
          const correctAns =
            currentQ.kind === 'mcq'
              ? currentQ.options[currentQ.correctAnswer]
              : currentQ.kind === 'fill'
                ? currentQ.correctAnswer
                : '';
          recordMistake(progress!, {
            questionId: deriveQuestionId(currentQ.prompt),
            kp: currentQ.kp,
            subject: opts.subject,
            prompt: currentQ.prompt,
            correctAnswer: correctAns,
            userAnswer: answer,
          });
          saveProgress(progress!);
        }
      }
      // proMode ON → 不顯示「+N ⚡」浮動 popup,也不顯示「沒關係 +2」遊戲化文案.
      // XP 仍正常累積進 PlayerProgress(finish() 那段 addFactionXP 處理).
      if (!pro) {
        if (result.xp > 0) {
          const faction = FACTIONS[factionForSubject(opts.subject)];
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(
            `+${Math.round(result.xp)} ${faction.emoji}`,
            opts.popupColor ?? '#ffd54f',
          );
        } else if (!result.correct) {
          showXpPopup('沒關係 +2', '#80cbc4');
        }
      }
      setTimeout(
        () => {
          if (lesson.state === 'done') finish();
          else renderCurrent();
        },
        result.correct ? 500 : 900,
      );
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    unmountEscape();
    onDone({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    const faction = factionForSubject(opts.subject);
    const xp = Math.round(lesson.totalXP);
    addFactionXP(progress!, faction, xp);
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    // proMode ON → silent (boss directive: 不需要有音樂).
    if (!pro && scoredTotal > 0) {
      const acc = scoredCorrect / scoredTotal;
      try {
        if (acc >= 0.6) getUiSoundPlayer().play('lesson-correct-fanfare', 0.55);
        else if (acc <= 0.3) getUiSoundPlayer().play('lesson-wrong-sting', 0.45);
      } catch {
        /* AudioContext busy / test harness */
      }
    }
    onDone({ newUnlocks });
  }

  renderCurrent();
}
